"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_router_dom_1 = require("react-router-dom");
const react_suspender_1 = __importDefault(require("react-suspender"));
const ui_1 = require("@mapmaker/ui");
const client_1 = require("@apollo/client");
const ErrorPage_1 = __importDefault(require("../../lib/errors/ErrorPage"));
const ProductPageContext_1 = require("./ProductPageContext");
require("./MapmakerProductPage.css");
const MapmakerApi_1 = require("../../client/MapmakerApi");
const useMapmakerTitle_1 = require("../../lib/hooks/useMapmakerTitle");
const ProductPageThumbnails_1 = __importDefault(require("./thumbnails/ProductPageThumbnails"));
const ProductBuyBox_1 = __importDefault(require("./ProductBuyBox"));
const FaChevronRight_1 = require("@react-icons/all-files/fa/FaChevronRight");
const FaTag_1 = require("@react-icons/all-files/fa/FaTag");
const isSameDay_1 = __importDefault(require("date-fns/isSameDay"));
(0, client_1.gql) `
  fragment StorefrontProductPageProduct on StorefrontProduct {
    id
    title
    descriptionHtml
    handle
    tags
    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }
    options {
      id
      name
      values
    }
    images(first: 30) {
      edges {
        node {
          ...StorefrontProductImage
        }
      }
    }
    variants(first: 100) {
      edges {
        node {
          ...StorefrontProductPageVariant
        }
      }
    }
    hideQuantity: metafield(namespace: "photomaps", key: "hideQuantity") {
      key
      value
    }
    personalizationType: metafield(
      namespace: "photomaps"
      key: "personalizationType"
    ) {
      key
      value
    }
    freeStickerTokens: metafield(
      namespace: "photomaps"
      key: "freeStickerTokens"
    ) {
      key
      value
    }
    mapmakerBlueprintId: metafield(namespace: "mapmaker", key: "blueprintId") {
      key
      value
    }
    mapmakerBackground: metafield(namespace: "mapmaker", key: "background") {
      key
      value
    }
    mapmakerOutputType: metafield(namespace: "mapmaker", key: "outputType") {
      key
      value
    }
    mapmakerOutputScale: metafield(namespace: "mapmaker", key: "outputScale") {
      key
      value
    }
    mapmakerDefaultInputs: metafield(
      namespace: "mapmaker"
      key: "defaultInputs"
    ) {
      key
      value
    }
  }

  fragment StorefrontProductPageVariant on StorefrontProductVariant {
    id
    sku
    title
    priceV2 {
      amount
      currencyCode
    }
    availableForSale
    image {
      ...StorefrontProductImage
    }
    selectedOptions {
      name
      value
    }
    personalizationType: metafield(
      namespace: "photomaps"
      key: "personalizationType"
    ) {
      key
      value
    }
    mapmakerBlueprintId: metafield(namespace: "mapmaker", key: "blueprintId") {
      key
      value
    }
    mapmakerBackground: metafield(namespace: "mapmaker", key: "background") {
      key
      value
    }
    mapmakerOutputType: metafield(namespace: "mapmaker", key: "outputType") {
      key
      value
    }
    mapmakerOutputScale: metafield(namespace: "mapmaker", key: "outputScale") {
      key
      value
    }
    mapmakerDefaultInputs: metafield(
      namespace: "mapmaker"
      key: "defaultInputs"
    ) {
      key
      value
    }
  }

  fragment StorefrontProductImage on StorefrontImage {
    id
    altText
    tinySrc: transformedSrc(maxWidth: 256)
    mediumSrc: transformedSrc(maxWidth: 1024)
    largeSrc: transformedSrc(maxWidth: 2048)
  }

  query storefrontGetProductByHandle($handle: String!) {
    product: storefrontProductByHandle(handle: $handle) {
      ...StorefrontProductPageProduct
    }
    stickerTokenProduct: storefrontProduct(handle: "sticker-tokens") {
      ...StickerTokenProduct
    }
  }
`;
function MapmakerProductPage({ handle, plugins = {}, children, }) {
    var _a;
    const { loading, data, error } = (0, MapmakerApi_1.useStorefrontGetProductByHandleQuery)({
        variables: {
            handle: handle,
        },
    });
    if (!handle) {
        return (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: "../" });
    }
    else if (loading) {
        return (0, jsx_runtime_1.jsx)(react_suspender_1.default, {});
    }
    else if (error || !(data === null || data === void 0 ? void 0 : data.product)) {
        // Before Apr. 2022 our most popular products had separate pages for light and dark. So those
        // URL's are floating around quite a bit still. Try to redirect in that case to the unified
        // handle.
        if (/(-light|-dark)$/i.test(handle)) {
            return ((0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: `/product/${handle.replace(/(-light|-dark)$/i, "")}` }));
        }
        return ((0, jsx_runtime_1.jsx)(ErrorPage_1.default, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ style: { textAlign: "center" } }, { children: [(0, jsx_runtime_1.jsx)("div", Object.assign({ style: { marginBottom: "1rem" } }, { children: (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : "Could not find product." })), (0, jsx_runtime_1.jsx)(ui_1.LinkButton, Object.assign({ to: "/shop", color: "accent" }, { children: "Browse Our Products" }))] })) }));
    }
    return ((0, jsx_runtime_1.jsx)(ProductPageContext_1.ProductPageContextProvider, Object.assign({ product: data.product, stickerTokensProduct: data.stickerTokenProduct, plugins: plugins }, { children: (0, jsx_runtime_1.jsx)(ProductPageWithContext, { children: children }) })));
}
exports.default = MapmakerProductPage;
function getSaleName() {
    const isBlackFriday = (0, isSameDay_1.default)(new Date(2022, 10, 25), new Date());
    const isCyberMonday = (0, isSameDay_1.default)(new Date(2022, 10, 28), new Date());
    if (isBlackFriday) {
        return "BLACK FRIDAY";
    }
    else if (isCyberMonday) {
        return "CYBER MONDAY";
    }
    else {
        return "HOLIDAY";
    }
}
function ProductPageWithContext({ children }) {
    const { product, freeStickerTokens } = (0, ProductPageContext_1.useProductPage)();
    (0, useMapmakerTitle_1.useMapmakerTitle)(product.title);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [freeStickerTokens > 0 && ((0, jsx_runtime_1.jsx)("div", Object.assign({ id: "product-page-promotion" }, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: "promotion-container" }, { children: [(0, jsx_runtime_1.jsxs)("h3", Object.assign({ className: "title" }, { children: [(0, jsx_runtime_1.jsx)(FaTag_1.FaTag, {}), " ", getSaleName(), " SALE!"] })), (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: "description" }, { children: ["This week only get", " ", freeStickerTokens === 1
                                    ? "1 FREE Sticker Tokens"
                                    : `${freeStickerTokens} FREE Sticker Tokens`, " ", "with the purchase of this ", product.title, ". Sticker Tokens are a great gift addition, or you can to use them on yourself!"] })), (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: "fine-print" }, { children: [(0, jsx_runtime_1.jsx)("div", Object.assign({ className: "" }, { children: "Offer ends 11/30/2022" })), (0, jsx_runtime_1.jsxs)(react_router_dom_1.Link, Object.assign({ to: "/help/sticker-token-promotion" }, { children: ["Details", (0, jsx_runtime_1.jsx)(FaChevronRight_1.FaChevronRight, { fontSize: 8 })] }))] }))] })) }))), (0, jsx_runtime_1.jsx)("div", Object.assign({ id: "mapmaker-product-page" }, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: "above-the-fold" }, { children: [(0, jsx_runtime_1.jsx)("section", Object.assign({ className: "images" }, { children: (0, jsx_runtime_1.jsx)(ProductPageThumbnails_1.default, {}) })), (0, jsx_runtime_1.jsx)("section", Object.assign({ className: "options" }, { children: (0, jsx_runtime_1.jsx)(ProductBuyBox_1.default, {}) }))] })) })), children] }));
}
//# sourceMappingURL=data:application/json;base64,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